import { useEffect, useMemo, useState } from "react";

import ChevronRight from "@/assets/icons/chevron-right.svg";
import { cx } from "@/utils";
import { dateMatchesRange } from "@/views/MentionsView/partials/DateRangeButton/ranges";
import { i18n } from "@lingui/core";

import CalendarBase from "../Calendar/CalendarBase";
import { CalendarContextProvider } from "../Calendar/CalendarContext";
import { useRangeCalendar } from "../Calendar/hooks/useRangeCalendar";
import { CalendarSettings } from "../Calendar/types";

import styles from "./Range.module.css";

export type DateRangePreset = {
  label: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  identifier?: string; // used for tracking purposes
  // default is used to indicate which date set is used when no start/end dates are specified.
  // Picking this will always clear the date range.
  default?: true;
};

type RangeProps = {
  className?: string;
  presets?: DateRangePreset[];
  settings?: Partial<CalendarSettings>;
  onChange: (
    dates: { startDate: Date | undefined; endDate: Date | undefined },
    fromPreset?: boolean
  ) => void;
  startDate?: Date;
  endDate?: Date;
  customRangeOpen?: boolean;
  onPresetChange?: (preset: DateRangePreset) => void;
};

const Range: React.FunctionComponent<RangeProps> = ({
  className,
  presets = [],
  settings = {},
  onChange,
  customRangeOpen: _customRangeOpen = false,
  startDate: initial_startDate = undefined,
  endDate: initial_endDate = undefined,
}) => {
  // The extra boolean passed in here
  const { startDate, endDate, handleDateChange } = useRangeCalendar<[boolean]>({
    startDate: initial_startDate,
    endDate: initial_endDate,
    onChange,
  });

  const [suggestCalendarOpen, setCalendarOpen] = useState(
    presets.length === 0 || // keep open if there are no presets passed in
      _customRangeOpen // Or if the user has explicitly set it to open
  );

  const range = useMemo(
    () =>
      startDate == undefined && endDate == undefined
        ? presets.find((preset) => preset.default)
        : presets.find((preset) =>
            dateMatchesRange(startDate, endDate, preset)
          ),
    [startDate, endDate, presets]
  );

  const calendarOpen = suggestCalendarOpen || !range;

  useEffect(() => {
    range && setCalendarOpen(false);
  }, [range]);

  return (
    <div className={cx(styles.root, className)} data-testid="datepicker">
      {/** Predefined sets */}
      {presets.length > 0 && (
        <div
          className={cx({
            [styles.presetOpen]: calendarOpen,
          })}
        >
          {presets.map(({ label, startDate, endDate, default: d }) => (
            <button
              className={cx(styles.preset, {
                [styles.active]: !suggestCalendarOpen && range?.label === label,
              })}
              key={label}
              onClick={() => {
                handleDateChange(
                  d
                    ? { startDate: undefined, endDate: undefined }
                    : { startDate, endDate },
                  true
                );
              }}
              data-testid={`datepicker--preset-${i18n._(label)}`}
            >
              {i18n._(label)}
            </button>
          ))}
          <button
            className={cx(styles.preset, {
              [styles.active]: calendarOpen,
            })}
            onClick={() => setCalendarOpen(true)}
            data-testid="datepicker--custom-range-button"
          >
            {L.DATE_CUSTOM}
            {!calendarOpen && <ChevronRight height="1em" />}
          </button>
        </div>
      )}
      {calendarOpen && (
        <CalendarContextProvider value={settings}>
          {/** Custom range - start date */}
          <CalendarBase
            value={startDate}
            onChange={(d) => handleDateChange({ startDate: d }, false)}
            label={L.DATE_CUSTOM_START_LABEL}
          />
          {/** Custom range - end date */}
          <CalendarBase
            value={endDate}
            onChange={(d) => handleDateChange({ endDate: d }, false)}
            label={L.DATE_CUSTOM_END_LABEL}
          />
        </CalendarContextProvider>
      )}
    </div>
  );
};

export default Range;
